export type DeleteMode = 'account_only' | 'account_and_content';

export interface DeletionReceipt {
  mode: DeleteMode;
  deleted: string[];
  retained: string[];
  pendingPurge: string[];
}

export interface ReceiptSection {
  count: number;
  labels: string[];
}

export interface DeletionReceiptSummary {
  headline: string;
  deleted: ReceiptSection;
  retained: ReceiptSection;
  pendingPurge: ReceiptSection;
}

const receiptLabels: Record<string, string> = {
  sessions: '로그인 상태',
  oauth_accounts: '연결한 로그인 계정',
  fit_profile: '내 좌석 조건',
  decisions: '저장한 좌석 결정',
  reviews: '내가 올린 리뷰',
  review_prices: '리뷰에 남긴 가격',
  corrections: '리뷰 정정 이력',
  anonymous_published_reviews: '작성자 정보를 없애고 남긴 게시 리뷰',
  proof_fingerprints_max_2y: '중복 게시·오용 방지 기록(최대 2년)',
  public_photos: '공개 사진',
  cached_derivatives: '사진을 보여주기 위해 만든 임시 사본',
};

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

export function parseDeletionReceipt(value: unknown): DeletionReceipt | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  if (candidate.mode !== 'account_only' && candidate.mode !== 'account_and_content') return null;
  if (!isStringArray(candidate.deleted) || candidate.deleted.length === 0) return null;
  if (!isStringArray(candidate.retained) || !isStringArray(candidate.pendingPurge)) return null;
  return {
    mode: candidate.mode,
    deleted: [...candidate.deleted],
    retained: [...candidate.retained],
    pendingPurge: [...candidate.pendingPurge],
  };
}

function summarizeSection(keys: readonly string[]): ReceiptSection {
  const uniqueKeys = [...new Set(keys)];
  const labels = uniqueKeys.flatMap((key) => receiptLabels[key] ? [receiptLabels[key]] : []);
  const unknownCount = uniqueKeys.length - labels.length;
  if (unknownCount > 0) labels.push(`그 밖의 데이터 ${unknownCount}종`);
  return { count: uniqueKeys.length, labels };
}

export function summarizeDeletionReceipt(receipt: DeletionReceipt): DeletionReceiptSummary {
  const deleted = summarizeSection(receipt.deleted);
  const retained = summarizeSection(receipt.retained);
  const pendingPurge = summarizeSection(receipt.pendingPurge);
  const keepsAnonymousReviews = receipt.retained.includes('anonymous_published_reviews');
  const headline = receipt.mode === 'account_only'
    ? keepsAnonymousReviews
      ? '계정을 지웠어요. 게시 리뷰는 작성자 정보 없이 남아요.'
      : '계정을 지웠어요. 아래에서 남은 데이터를 확인해 주세요.'
    : pendingPurge.count > 0
      ? `계정과 게시물을 지웠고, 남은 데이터 ${pendingPurge.count}종은 지우는 중이에요.`
      : '계정과 게시물을 지웠어요.';
  return { headline, deleted, retained, pendingPurge };
}
